import { VariantProps } from "class-variance-authority";
import React, { useState } from "react";

import TerminateFormModal from "../components/Termination/TerminateForm";
import TerminationSheet from "../components/Termination/TerminationSheet";
import TicketSheet from "../components/Termination/TicketSheet";
import AddEmployeeModal from "../components/dialogs/AddEmployee";
import UpdateEmployeeModal from "../components/dialogs/UpdateEmployee";

import { badgeVariants } from "@/components/ui/badge";
import { filterItem } from "@/hooks/useGetFilter";
import useQueryID from "@/hooks/useQueryId";
import { ICustomer } from "@/interfaces/customer.interface";
import {
  IEmployeeOptionsList,
  TApprovedStatus,
} from "@/interfaces/employee.interface";
import { EmploymentType } from "@/interfaces/globals";
import { isAdminDomain } from "@/lib/utils";
import { useGetAllCustomerByWSQuery } from "@/redux/api/clientQuery";
import {
  useGetEmployeeOptionsQuery,
  useGetSingleEmployeeQuery,
} from "@/redux/api/employeeQuery";
import {
  EmployeeRole,
  TAddEmployeeForm,
  TemployeeWorkInfoSchema,
} from "@/schemas/employee-schema";

type TEmployeeCtx = {
  customerList: filterItem[];
  addEmployeeModal: boolean;
  setAddEmployeeModal: (value: boolean) => void;
  updateEmployee?: boolean;
  setUpdateEmployee: (value: boolean) => void;
  employeeId?: string;
  basicInfo?: TAddEmployeeForm;
  workFormInfo?: TemployeeWorkInfoSchema;
  isImportantModal: boolean;
  setIsImportantModal: (value: boolean) => void;
  skillsList: IEmployeeOptionsList[] | undefined;
  isLoading: boolean;
  getStatusColor: (
    status?: TApprovedStatus,
    isDeviceId?: string,
  ) => VariantProps<typeof badgeVariants>["variant"];
  terminationSheet: boolean;
  setTerminationSheet: React.Dispatch<React.SetStateAction<boolean>>;
  isTerminate: boolean;
  setIsTerminate: React.Dispatch<React.SetStateAction<boolean>>;
  ticketSheet: boolean;
  setTicketSheet: React.Dispatch<React.SetStateAction<boolean>>;
};

const employeeCtx = React.createContext<TEmployeeCtx>({} as TEmployeeCtx);

export const useEmployeeContext = (): TEmployeeCtx => {
  return React.useContext(employeeCtx);
};

export const EmployeeContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => {
  const [addEmployeeModal, setAddEmployeeModal] = useState<boolean>(false);
  const [updateEmployee, setUpdateEmployee] = useState<boolean>(false);
  const [isImportantModal, setIsImportantModal] = useState<boolean>(false);

  const [terminationSheet, setTerminationSheet] = useState<boolean>(false);
  const [isTerminate, setIsTerminate] = useState<boolean>(false);
  const [ticketSheet, setTicketSheet] = useState<boolean>(false);

  const { data: customers } = useGetAllCustomerByWSQuery(
    {
      user: "",
      status: "ACTIVE",
    },
    {
      skip: isAdminDomain,
    },
  );

  const { data: skillsList } = useGetEmployeeOptionsQuery(
    {
      filters: "",
    },
    {
      skip: isAdminDomain,
    },
  );

  const customerList =
    (customers as GeneralResponse<ICustomer[]>)?.data?.map((item) => ({
      label: item?.name || "",
      id: item.id || "",
    })) || [];

  const { id: employeeId } = useQueryID("id");
  const { data, isLoading } = useGetSingleEmployeeQuery(
    {
      employeeId,
    },
    {
      skip: (!addEmployeeModal || !updateEmployee) && !employeeId,
    },
  );

  const basicInfo: TAddEmployeeForm = {
    firstName: data?.user?.firstName,
    middleName: data?.user?.middleName ?? "",
    lastName: data?.user?.lastName,
    email: data?.user?.email ?? "",
    phoneNumber: data?.user?.phoneNumber,
    address: data?.address,
    whiteCardNo: data?.whiteCardNo,
    describesYou: data?.describesYou,
  };

  const workFormInfo: TemployeeWorkInfoSchema = {
    employmentSeeking: {
      employmentType: data?.workInfo?.employmentSeeking
        ?.employmentType as EmploymentType,
      desiredRole: data?.workInfo?.employmentSeeking
        ?.desiredRole as EmployeeRole,
      proposedStartDate: data?.workInfo?.employmentSeeking?.proposedStartDate,
    },
    previousExperience: {
      trainings: (data?.workInfo?.previousExperience?.trainings ?? []).map(
        (t) => ({
          label: t?.name ?? "",
          value: t?.name ?? "",
        }),
      ),
      referenceDetails:
        data?.workInfo?.previousExperience.referenceDetails ?? "",
      skills: data?.workInfo?.previousExperience.skills ?? [],
    },
    trainings: (data?.workInfo?.previousExperience?.trainings ?? []).map(
      (t) => ({
        name: t.name,
        issuedDate: t.issuedDate,
        expiryDate: t.expiryDate,
        image: t?.ticketUrl ? [t?.ticketUrl] : [],
      }),
    ),
  };

  const getStatusColor = (
    status?: TApprovedStatus,
    isDeviceId?: string,
  ): VariantProps<typeof badgeVariants>["variant"] => {
    if (status === "APPROVED") {
      return "success";
    } else if (isDeviceId && status !== ("APPROVED" as TApprovedStatus)) {
      return "lightViolet";
    } else if (status === ("PENDING" as TApprovedStatus)) {
      return "yellow";
    } else {
      return "default";
    }
  };

  return (
    <employeeCtx.Provider
      value={{
        customerList,
        addEmployeeModal,
        setAddEmployeeModal,
        updateEmployee,
        setUpdateEmployee,
        employeeId,
        basicInfo,
        workFormInfo,
        isImportantModal,
        setIsImportantModal,
        skillsList,
        isLoading,
        getStatusColor,
        terminationSheet,
        setTerminationSheet,
        isTerminate,
        setIsTerminate,
        ticketSheet,
        setTicketSheet,
      }}
    >
      <>
        {children}

        {addEmployeeModal && (
          <AddEmployeeModal
            isOpen={addEmployeeModal}
            toggleModal={() => setAddEmployeeModal(!addEmployeeModal)}
          />
        )}
        {updateEmployee && (
          <UpdateEmployeeModal
            isOpen={updateEmployee}
            toggleModal={() => setUpdateEmployee(!updateEmployee)}
          />
        )}

        {isTerminate && (
          <TerminateFormModal
            isOpen={isTerminate}
            toggleModal={() => setIsTerminate(!isTerminate)}
          />
        )}
        {terminationSheet && (
          <TerminationSheet
            isOpen={terminationSheet}
            toggleOpen={() => setTerminationSheet(!terminationSheet)}
          />
        )}
        {ticketSheet && (
          <TicketSheet
            isOpen={ticketSheet}
            toggleOpen={() => setTicketSheet(!ticketSheet)}
          />
        )}
      </>
    </employeeCtx.Provider>
  );
};
